import { OrderEntryUnit } from "~/gql/graphqlBase.generated";
import formatLocaleNumber from "~/helpers/localeNumberFormat";
import MoneyFormat from "~/helpers/moneyFormat";
import orderLineUnits from "~fulfill/orders/helpers/orderLineUnits";
import pricingBasisLabels from "~fulfill/product/helpers/pricingBasisLabels";
import shippingUomLabels from "~fulfill/product/helpers/shippingUomLabels";
import { tPortal } from "~portal/helpers/portalDisplayFormatters";

export interface PortalEntryUnitSource {
	entryEachesPerUnit?: null | number;
	shippingUomLabel?: null | string;
}

export interface PortalOrderLineQuantity {
	eachesPerUnit?: null | number;
	orderEntryUnit?: null | OrderEntryUnit;
	orderedQuantity?: null | number;
	orderedUnits?: null | number;
	shippingUomLabel?: null | string;
}

export interface PortalOrderLinePrice extends PortalOrderLineQuantity {
	currency?: null | string;
	price: number;
}

export interface PortalQuantityResolution {
	cappedFrom: null | number;
	eaches: number;
	roundedFrom: null | number;
	units: null | number;
}

export interface PortalLineWireQuantities {
	eachesQuantity?: number;
	quantity: number;
}

export interface PortalLineQuantitySource extends PortalEntryUnitSource {
	quantity?: null | number;
}

const ONE_EACH_PER_UNIT = 1;

const wholePackAbove = (value: null | number | undefined, floor: number): null | number =>
	value != null && Number.isFinite(value) && value > floor ? Math.floor(value) : null;

const wholeCount = (value: null | number | undefined): number =>
	value != null && Number.isFinite(value) ? Math.max(Math.trunc(value), 0) : 0;

const roundDownToWholePacks = (eaches: number, pack: number): number => Math.floor(eaches / pack) * pack;

export const portalEntryEachesPerUnit = (
	entryUnit: null | OrderEntryUnit | undefined,
	eachesPerUnit: null | number | undefined
): null | number =>
	entryUnit === OrderEntryUnit.ShippingUom ? wholePackAbove(eachesPerUnit, ONE_EACH_PER_UNIT) : null;

export const portalEntryPack = (source: null | PortalEntryUnitSource | undefined): number =>
	wholePackAbove(source?.entryEachesPerUnit, ONE_EACH_PER_UNIT) ?? ONE_EACH_PER_UNIT;

export const isPortalUnitEntry = (source: null | PortalEntryUnitSource | undefined): boolean =>
	portalEntryPack(source) > ONE_EACH_PER_UNIT;

export const isPortalUnitEntryBuyer = (
	account: null | { effectiveOrderEntryUnit?: null | OrderEntryUnit } | undefined
): boolean => account?.effectiveOrderEntryUnit === OrderEntryUnit.ShippingUom;

export const portalMaxOrderableEaches = (
	availableEaches: null | number | undefined,
	source: null | PortalEntryUnitSource | undefined
): number => roundDownToWholePacks(wholeCount(availableEaches), portalEntryPack(source));

export const portalMinOrderableEaches = (
	minOrderQty: null | number | undefined,
	source: null | PortalEntryUnitSource | undefined
): number => {
	const pack = portalEntryPack(source);
	return Math.max(Math.ceil(Math.max(wholeCount(minOrderQty), ONE_EACH_PER_UNIT) / pack), ONE_EACH_PER_UNIT) * pack;
};

export const clampPortalCartQuantity = (
	quantity: null | number | undefined,
	availableEaches: null | number | undefined,
	source: null | PortalEntryUnitSource | undefined
): number => resolvePortalQuantity(quantity, availableEaches, source).eaches;

export const portalUnitsFromEaches = (
	eaches: null | number | undefined,
	source: null | PortalEntryUnitSource | undefined
): null | number => (isPortalUnitEntry(source) ? Math.floor(wholeCount(eaches) / portalEntryPack(source)) : null);

export const portalEachesFromUnits = (
	units: null | number | undefined,
	source: null | PortalEntryUnitSource | undefined
): number => wholeCount(units) * portalEntryPack(source);

export const portalEntryQuantity = (
	eaches: null | number | undefined,
	source: null | PortalEntryUnitSource | undefined
): number => portalUnitsFromEaches(eaches, source) ?? wholeCount(eaches);

export const portalShipRuleQuantity = (
	eaches: null | number | undefined,
	source: null | PortalEntryUnitSource | undefined,
	eachesPerUnit: null | number | undefined
): number =>
	isPortalUnitEntry(source)
		? portalEntryQuantity(eaches, source)
		: Math.ceil(wholeCount(eaches) / Math.max(Math.floor(eachesPerUnit ?? 1), 1));

export type PortalEntryField = "eaches" | "entry";

export const wholePortalEntryQuantity = (
	typed: null | number,
	field: PortalEntryField,
	source: null | PortalEntryUnitSource | undefined
): null | number => {
	if (typed == null || !Number.isInteger(typed)) return null;
	if (field === "entry") return typed;
	const pack = portalEntryPack(source);
	return typed % pack === 0 ? typed / pack : null;
};

export const linkedPortalEntryValue = (
	typed: null | number,
	field: PortalEntryField,
	source: null | PortalEntryUnitSource | undefined
): null | number => {
	const entry = wholePortalEntryQuantity(typed, field, source);
	if (entry == null || field === "eaches") return entry;
	return entry * portalEntryPack(source);
};

export interface PortalEntryDraft {
	field: PortalEntryField;
	text: string;
}

export const typedQuantity = (text: string): null | number => (text.trim() === "" ? null : Number(text));

export const draftText = (
	{ field, text }: PortalEntryDraft,
	shownField: PortalEntryField,
	source: null | PortalEntryUnitSource | undefined
): string => {
	if (field === shownField) return text;
	const linked = linkedPortalEntryValue(typedQuantity(text), field, source);
	return linked == null ? "" : String(linked);
};

export const portalLineWireQuantities = (
	source: null | PortalLineQuantitySource | undefined
): PortalLineWireQuantities => {
	const units = portalUnitsFromEaches(source?.quantity, source);
	// The server reads a bare `quantity` as shipping units, so a unit-entry line must state its unit count.
	if (units == null) return { quantity: wholeCount(source?.quantity) };
	return { eachesQuantity: units, quantity: portalEachesFromUnits(units, source) };
};

export const resolvePortalQuantity = (
	requestedEaches: null | number | undefined,
	availableEaches: null | number | undefined,
	source: null | PortalEntryUnitSource | undefined
): PortalQuantityResolution => {
	const pack = portalEntryPack(source);
	const requested = wholeCount(requestedEaches);
	const capped = Math.min(requested, wholeCount(availableEaches));
	const eaches = roundDownToWholePacks(capped, pack);
	return {
		cappedFrom: capped < requested ? requested : null,
		eaches,
		roundedFrom: eaches < capped ? capped : null,
		units: pack > ONE_EACH_PER_UNIT ? eaches / pack : null
	};
};

export const formatPortalQuantityFromEaches = (
	eaches: null | number | undefined,
	source: null | PortalEntryUnitSource | undefined
): string => {
	const units = portalUnitsFromEaches(eaches, source);
	if (units == null) return formatLocaleNumber(wholeCount(eaches));
	return shippingUomLabels.unitCountWithEaches(units, wholeCount(eaches), source?.shippingUomLabel);
};

export const formatPortalQuantityFromUnits = (
	units: null | number | undefined,
	source: null | PortalEntryUnitSource | undefined
): string => formatPortalQuantityFromEaches(portalEachesFromUnits(units, source), source);

const portalOrderLineUnits = (line: null | PortalOrderLineQuantity | undefined): null | number => {
	const pack = portalEntryEachesPerUnit(line?.orderEntryUnit, line?.eachesPerUnit);
	return pack == null || line?.orderedUnits == null ? null : wholeCount(line.orderedUnits);
};

export const formatPortalOrderLineQuantity = (line: null | PortalOrderLineQuantity | undefined): string => {
	const eaches = wholeCount(line?.orderedQuantity);
	const units = portalOrderLineUnits(line);
	if (units == null) return formatLocaleNumber(eaches);
	return shippingUomLabels.unitCountWithEaches(units, eaches, line?.shippingUomLabel);
};

export const formatPortalOrderLinePrice = (line: PortalOrderLinePrice): string => {
	const price = MoneyFormat(line.price, line.currency || "USD");
	return portalOrderLineUnits(line) != null && line.shippingUomLabel
		? pricingBasisLabels.pricePerEachShort(price)
		: price;
};

export const portalQuantityAdjustedMessage = (
	resolution: PortalQuantityResolution,
	source: null | PortalEntryUnitSource | undefined
): null | string => {
	if (resolution.roundedFrom == null) return null;
	return shippingUomLabels.roundedDownToWholeUnits(
		shippingUomLabels.unitCountWithEaches(
			orderLineUnits.truncateUnits(resolution.roundedFrom, portalEntryPack(source)),
			resolution.roundedFrom,
			source?.shippingUomLabel
		),
		formatPortalQuantityFromEaches(resolution.eaches, source)
	);
};

export const portalQuantityCappedMessage = (
	requested: number,
	available: number,
	source: null | PortalEntryUnitSource | undefined
): string =>
	tPortal("cart.quantity.capped", {
		available: formatPortalQuantityFromUnits(available, source),
		requested: formatPortalQuantityFromUnits(requested, source)
	});

export const portalAddedToCartMessage = (
	addedEntryQty: number,
	source: null | PortalEntryUnitSource | undefined
): string =>
	isPortalUnitEntry(source)
		? tPortal("cart.quantity.addedUnits", { quantity: formatPortalQuantityFromUnits(addedEntryQty, source) })
		: tPortal("cart.quantity.added", { count: addedEntryQty });

export const portalEntryRefusedMessage = (): string => tPortal("fulfill:inventory.units.wholeNumbersOnly");

export interface PortalCartAdjustmentCounts {
	cappedCount?: number;
	droppedCount?: number;
	priceUnavailableCount?: number;
	roundedCount?: number;
}

export const portalCartAdjustmentMessages = ({
	cappedCount = 0,
	droppedCount = 0,
	priceUnavailableCount = 0,
	roundedCount = 0
}: PortalCartAdjustmentCounts): string[] => [
	...(priceUnavailableCount > 0
		? [tPortal("cart.quantity.priceUnavailableSkipped", { count: priceUnavailableCount })]
		: []),
	...(droppedCount > 0 ? [shippingUomLabels.unorderableLineCount(droppedCount)] : []),
	...(cappedCount > 0 ? [shippingUomLabels.cappedLineCount(cappedCount)] : []),
	...(roundedCount > 0 ? [shippingUomLabels.roundedDownLineCount(roundedCount)] : [])
];

const portalEntryUnit = {
	clampPortalCartQuantity,
	draftText,
	formatPortalOrderLinePrice,
	formatPortalOrderLineQuantity,
	formatPortalQuantityFromEaches,
	formatPortalQuantityFromUnits,
	isPortalUnitEntry,
	isPortalUnitEntryBuyer,
	linkedPortalEntryValue,
	portalAddedToCartMessage,
	portalCartAdjustmentMessages,
	portalEachesFromUnits,
	portalEntryEachesPerUnit,
	portalEntryPack,
	portalEntryQuantity,
	portalEntryRefusedMessage,
	portalLineWireQuantities,
	portalMaxOrderableEaches,
	portalMinOrderableEaches,
	portalQuantityAdjustedMessage,
	portalQuantityCappedMessage,
	portalShipRuleQuantity,
	portalUnitsFromEaches,
	resolvePortalQuantity,
	typedQuantity,
	wholePortalEntryQuantity
};

export default portalEntryUnit;
